import { SpecialtyPill } from '@/components/atoms';

/**
 * SpecialtyFilter — Component
 * Multi-select pill group for filtering providers by specialty (mirrors
 * Flutter's `selectedSpeciality` list).
 *
 * Behavior:
 *   - "All" pill = "no filter" state. Tapping it clears the selection.
 *     It's visually selected when `selected` is empty.
 *   - Any other pill toggles itself in/out of the selection (multi-select).
 *   - The pills array MUST start with "All".
 */

interface SpecialtyFilterProps {
  /** Pill labels in display order; "All" must be first. */
  specialties: string[];
  /** Currently selected specialty labels. Empty array = "All". */
  selected: string[];
  /** Toggle a specialty in/out of the selection. */
  onToggle: (specialty: string) => void;
  /** Clear the selection (called when "All" is tapped). */
  onClear: () => void;
  /** Accessible name of the pill group. Defaults to the specialty wording. */
  ariaLabel?: string;
}

export function SpecialtyFilter({
  specialties,
  selected,
  onToggle,
  onClear,
  ariaLabel = 'Filter by specialty',
}: SpecialtyFilterProps) {
  return (
    <div className="flex gap-1 flex-wrap" role="listbox" aria-label={ariaLabel}>
      {specialties.map((s) => {
        const isAll = s === 'All';
        const isSelected = isAll ? selected.length === 0 : selected.includes(s);
        return (
          <SpecialtyPill
            key={s}
            label={s}
            isSelected={isSelected}
            onClick={() => (isAll ? onClear() : onToggle(s))}
          />
        );
      })}
    </div>
  );
}
